import React, { useEffect, useState, useCallback } from "react";
import {
  useTicketDetailsAfterSelectingDispositionQuery,
  useUpdateTicketWorkflowMutation,
} from "../../Services/ticket/CreateTicket";
import { useReplyMailMutation } from "../../Services/email/EmailInbox";
import {
  InputLabel,
  ListItemText,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { PhoneInput } from "react-international-phone";
import { capitalizeFirstLetter } from "../reusableComponent/CapitalLetter";
import { toast } from "react-toastify";
import { Loader } from "react-bootstrap-typeahead";
import { v4 as uuidv4 } from "uuid";

interface IPROPS {
  closeModal: any;
  ticketId: any;
  dynamicData: any;
  fetchDispositionStatus: any;
  refetch: any;
}

interface ErrorState {
  [key: string]: string;
}

const EmailDispositionWorkflowModal: React.FC<IPROPS> = ({
  closeModal,
  ticketId,
  dynamicData,
  fetchDispositionStatus,
  refetch,
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
    const formattedTime = date.toLocaleTimeString("en-GB"); // HH:mm:ss format
    return `${formattedDate}, ${formattedTime}`;
  };

  const [internalNotes, setInternalNotes] = useState([]);
  const [workFlowLoader, setWorkflowLoader] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  const [status, setStatus] = useState("");
  const [dynamicFields, setDynamicFields] = useState<any>({});
  const [statusId, setStatusId] = useState("");
  const [errors, setErrors] = useState<ErrorState>({});
  const [selectedStatusId, setSelectedStatusId] = useState<any>("");
  const [originalFields, setOriginalFields] = useState({});
  const [statusOptions, setStatusOptions] = useState<any>([]);
  const [originalStatus, setOriginalStatus] = useState("");

  const { data } = useTicketDetailsAfterSelectingDispositionQuery(ticketId);
  const [replyMail, { status: replyStatus }] = useReplyMailMutation();
  const [trigger, { isLoading, status: ticketWorkflow }] =
    useUpdateTicketWorkflowMutation();

  useEffect(() => {
    if (ticketWorkflow === "pending") {
      setWorkflowLoader(true);
    } else {
      setWorkflowLoader(false);
    }
  }, [ticketWorkflow]);

  // Check if form has changes
  const checkFormChanges = useCallback(() => {
    // Check if dynamic fields have changed
    const fieldsChanged = Object.keys(dynamicFields).some((key) => {
      const currentValue = dynamicFields[key];
      const originalValue = originalFields[key];

      // Handle different data types
      if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
        return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
      }

      return currentValue !== originalValue;
    });

    // Check if status has changed
    const statusChanged = selectedStatusId !== originalStatus;

    const hasChanges = fieldsChanged || statusChanged;
    setHasFormChanges(hasChanges);

    return hasChanges;
  }, [dynamicFields, originalFields, selectedStatusId, originalStatus]);

  const handleInputChange = useCallback((label, value) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[label]; // Remove error for this field
      return newErrors;
    });

    setDynamicFields((prevState) => ({
      ...prevState,
      [label]: value,
    }));
  }, []);

  useEffect(() => {
    checkFormChanges();
  }, [checkFormChanges]);

  useEffect(() => {
    if (data) {
      const dynamicFieldsArray = Object.entries(
        data.dynamic_fields || {}
      ).reduce((acc, [label, value]) => ({ ...acc, [label]: value }), {});

      const fetchingStatusValue = data?.status_display
        ? data?.status_display
        : "";

      setDynamicFields(dynamicFieldsArray);
      setOriginalFields(dynamicFieldsArray);

      // Set original status for comparison
      if (data?.status) {
        setOriginalStatus(data.status);
      }

      const formattedInternalNotes = data?.internal_notes?.map((el) => ({
        content: el?.content,
        created: formatDate(el?.created),
        firstName: el?.created_By?.first_name,
        lastName: el?.created_By?.last_name,
      }));
      setInternalNotes(formattedInternalNotes || []);
    }
  }, [data]);

  const validateForm = () => {
    const newErrors: ErrorState = {};

    // Validate dynamic fields
    dynamicData?.forEach((field: any) => {
      const fieldValue = dynamicFields[field.label];

      if (
        field.is_required &&
        (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0))
      ) {
        newErrors[field.label] = `${field.label} is required`;
      }

      // Phone number validation
      if (field.is_required && field.field_type === "tel-phone" && fieldValue) {
        const isValidPhone =
          /^(?:\+?\d{1,3})?[ -]?\(?\d{1,4}?\)?[ -]?\d{1,4}[ -]?\d{1,4}(?: x\d{1,4})?$/.test(
            fieldValue
          );
        if (!isValidPhone) {
          newErrors[
            field.label
          ] = `${field.label} must be a valid phone number`;
        }
      }

      // Email validation
      if (field.field_type === "email" && fieldValue) {
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fieldValue);
        if (!isValidEmail) {
          newErrors[
            field.label
          ] = `${field.label} must be a valid email address`;
        }
      }

      // Text area validation for remarks
      if (
        field.field_type === "text-area" &&
        field.label === "remarks" &&
        field.is_required &&
        !fieldValue
      ) {
        newErrors[field.label] = `${field.label} is required`;
      }
    });

    // Status validation
    if (!selectedStatusId) {
      newErrors.status_type = "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveHandler = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (!hasFormChanges) {
      toast.info("No changes detected.");
      return;
    }

    // Prepare dynamic fields - include all fields that have values or have changed
    const editedFields = Object.keys(dynamicFields).reduce((acc, key) => {
      const field = dynamicData.find((field) => field.label === key);
      const currentValue = dynamicFields[key];
      const originalValue = originalFields[key];

      // Include field if it has a value or has changed
      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ""
      ) {
        if (field?.field_type === "date" && currentValue) {
          const formattedDate = new Date(currentValue)
            .toISOString()
            .replace("Z", "+00:00");
          acc[key] = formattedDate;
        } else if (field?.field_type === "date-time" && currentValue) {
          const formattedDateTime = new Date(currentValue)
            .toISOString()
            .replace("Z", "+00:00");
          acc[key] = formattedDateTime;
        } else {
          acc[key] = currentValue;
        }
      } else if (currentValue !== originalValue) {
        // Include changed fields even if they're empty (for clearing values)
        acc[key] = currentValue || "";
      }

      return acc;
    }, {});

    const updateWorkflowObj = {
      status: status === "wip" ? statusId : null,
      closed_status: status === "closed" ? statusId : null,
      status_type: status,
      dynamic_fields: editedFields,
    };

    try {
      const response = await trigger({
        id: ticketId,
        body: updateWorkflowObj,
      });

      if (response.data) {
        closeModal();
        refetch();
        toast.success("Updated Successfully");
      } else if (
        "error" in response &&
        response.error &&
        "data" in response.error
      ) {
        const errorData = response?.error?.data as {
          detail?: string[];
          message?: string;
        };
        const errorMessage =
          errorData?.detail?.[0] || errorData?.message || "An error occurred";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("An error occurred while saving");
    }
  };

  const handleStatusChange = (value) => {
    if (value) {
      const matchedStatus = statusOptions.find(
        (status) => status.rid === value
      );
      if (matchedStatus) {
        setSelectedStatusId(value);
        setStatus(matchedStatus.status_type);
        setStatusId(matchedStatus.id);

        // Clear status error
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.status_type;
          return newErrors;
        });
      }
    } else {
      setSelectedStatusId("");
      setStatus("");
      setStatusId("");
    }
  };

  const getDataTimeFormatted = (date) => {
    if (date && date.length > 0) {
      const dateObj = new Date(date);
      const year = dateObj.getUTCFullYear();
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getUTCDate()).padStart(2, "0");
      const hours = String(dateObj.getUTCHours()).padStart(2, "0");
      const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      return formattedDate;
    } else {
      return "";
    }
  };

  const formatStatusData = (status) => {
    const generateUUID = () => uuidv4();

    if (typeof status.status === "string") {
      return {
        status_type: status.status_type,
        status: status.status,
        id: null,
        rid: generateUUID(),
      };
    } else {
      return {
        id: status?.status?.id,
        status_type: status?.status_type,
        status: status?.status?.status,
        rid: generateUUID(),
      };
    }
  };

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<{ value: any }>, key: string) => {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });

      setDynamicFields((prevFields) => ({
        ...prevFields,
        [key]: event.target.value,
      }));
    },
    []
  );

  useEffect(() => {
    if (fetchDispositionStatus?.all_statuses) {
      const statusOptions1 = fetchDispositionStatus.all_statuses.map((status) =>
        formatStatusData(status)
      );
      setStatusOptions(statusOptions1);
    }
  }, [fetchDispositionStatus]);

  return (
    <div style={{ maxHeight: "60vh", marginBottom: "10px" }}>
      <div
        style={{
          maxHeight: "50vh",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div
          style={{
            marginTop: "15px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {dynamicData?.map((field: any) => (
            <React.Fragment key={field?.label}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <InputLabel htmlFor={`dynamic_fields.${field.label}`}>
                  {field.label}
                  {field.is_required && <span className="required"> *</span>}
                </InputLabel>

                {field.field_type === "text" && (
                  <TextField
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                    error={!!errors[field.label]}
                  />
                )}

                {field.field_type === "email" && (
                  <TextField
                    type="email"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                    error={!!errors[field.label]}
                  />
                )}

                {field.field_type === "number" && (
                  <TextField
                    type="number"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                    error={!!errors[field.label]}
                  />
                )}

                {field.field_type === "date" && (
                  <TextField
                    type="date"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    required={field.is_required}
                    error={!!errors[field.label]}
                  />
                )}

                {field.field_type === "time" && (
                  <TextField
                    type="time"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    required={field.is_required}
                    error={!!errors[field.label]}
                  />
                )}

                {field.field_type === "date-time" && (
                  <TextField
                    type="datetime-local"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={
                      getDataTimeFormatted(dynamicFields[field.label]) || ""
                    }
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    required={field.is_required}
                    error={!!errors[field.label]}
                  />
                )}

                {field.field_type === "text-area" && (
                  <TextField
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                    error={!!errors[field.label]}
                  />
                )}

                {field.field_type === "drop-down" && (
                  <FormControl fullWidth error={!!errors[field.label]}>
                    <Select
                      labelId={`select-label-${field.label}`}
                      id={`select-${field.label}`}
                      value={dynamicFields[field.label] || ""}
                      onChange={(e) =>
                        handleInputChange(field.label, e.target.value)
                      }
                      required={field.is_required}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {field?.choices?.map((option: any) => (
                        <MenuItem key={option.id} value={option.choice}>
                          {option.choice}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {field.field_type === "check-box" && (
                  <FormControl fullWidth error={!!errors[field.label]}>
                    <Select
                      labelId={`checkbox-label-${field.label}`}
                      id={`checkbox-${field.label}`}
                      required={field.is_required}
                      multiple
                      value={dynamicFields[field.label] || []}
                      onChange={(e: any) =>
                        handleCheckboxChange(e, field.label)
                      }
                      renderValue={(selected) => {
                        const selectedLabels = (field?.multiple_choices || [])
                          .filter((choice) =>
                            selected.includes(String(choice.id))
                          )
                          .map((choice) => choice.choice);
                        return selectedLabels.join(", ");
                      }}
                    >
                      {field?.multiple_choices?.map((choice: any) => (
                        <MenuItem key={choice.id} value={String(choice.id)}>
                          <Checkbox
                            checked={
                              dynamicFields[field.label]?.includes(
                                String(choice.id)
                              ) || false
                            }
                          />
                          <ListItemText primary={choice.choice} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {field.field_type === "tel-phone" && (
                  <PhoneInput
                    defaultCountry="IN"
                    value={dynamicFields[field.label] || "+91"}
                    onChange={(value) => handleInputChange(field.label, value)}
                    required={field.is_required}
                  />
                )}

                {errors[field.label] && (
                  <div className="mt-2" style={{ color: "red" }}>
                    {capitalizeFirstLetter(errors[field.label])}
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div
          style={{ width: "100%", display: "flex", flexDirection: "column" }}
        >
          <label>Internal Notes</label>
          <input className="form-control" />
        </div>

        {internalNotes?.map((el, index) => (
          <div key={index} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <label>
                <span>{el?.firstName}</span> <span>{el?.lastName}</span>
              </label>
              <label>{el?.created}</label>
            </div>
            <div>{el?.content}</div>
          </div>
        ))}

        <div className="row g-5 g-xl-8 mb-4">
          <div className="col-xl-6" style={{ width: "100%" }}>
            <label className="required">Status</label>
            <select
              onChange={(e) => handleStatusChange(e.target.value)}
              value={selectedStatusId || ""}
              className="form-select form-select-lg form-control-solid"
            >
              <option value="" selected>
                Select Status
              </option>
              {statusOptions?.map((statusItem) => (
                <option key={statusItem.rid} value={statusItem.rid}>
                  {statusItem.status}
                </option>
              ))}
            </select>
            {errors.status_type && (
              <div className="text-danger">{errors.status_type}</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", width: "100%", justifyContent: "end" }}>
        <button
          className="btn btn-primary"
          onClick={saveHandler}
          disabled={!hasFormChanges || workFlowLoader}
        >
          {workFlowLoader ? (
            <>
              Saving...
              <Loader />
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailDispositionWorkflowModal;
